import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import albumCompact from "/images/products/pix.png";
import albumStandard from "/images/products/carre.png";
import albumPremium from "/images/products/paysage.png";
import albumVoyage from "/images/products/portrait.png";
import albumMariage from "/images/products/tirage.png";
import albumNaissance from "/images/products/yearbook.png";

const products = [
  {
    name: "Le Pix",
    format: "13 x 13 cm",
    pages: "80 pages max",
    features: ["Couverture semi-rigide", "Papier photo premium"],
    price: "16€",
    originalPrice: "20€",
    discount: "-20%",
    badge: "Mini",
    badgeColor: "bg-[#f9bb72]",
    image: "/images/products/pix.png",
    link: "/create_book?open=1",
  },
    {
    name: "L'Album Carré",
    format: "21 x 21 cm",
    pages: "30 x 30 cm",
    features: ["Couverture rigide", "De 24 à 160 pages"],
    price: "27,99€",
    originalPrice: "34,99€",
    discount: "-20%",
    badge: "Best-seller",
    badgeColor: "bg-blue-400",
    image: "/images/products/carre.png",
    link: "/create_book?open=2",
  },
  {
    name: "L'Album Paysage",
    format: "A4",
    pages: "A5",
    features: ["Couverture rigide", "De 24 à 160 pages"],
    price: "27,99€",
    originalPrice: "34,99€",
    discount: "-20%",
    badge: "",
    badgeColor: "bg-pink-500",
    image: "/images/products/paysage.png",
    link: "/create_book?open=7",
  },


  {
    name: "L'Album PREMIUM",
    format: "20 x 20 cm",
    pages: "29 x 29 cm",
    features: ["Couverture rigide/Ouverture à plat", "Papier photo premium"],
    price: "39,99€",
    originalPrice: "49,99€",
    discount: "-20%",
    badge: "Premium",
    badgeColor: "bg-green-500",
    image: "/images/products/portrait.png",
    link: "/create_book?open=8",
  },
  {
    name: "Les Tirages Photos",
    format: "10 x 10 cm",
    pages: "10 x 15 cm",
    features: ["Papier photo premium", "De 10 à 100 tirages"],
    price: "25€",
    originalPrice: "30€",
    discount: "-20%",
    badge: "Nouveauté",
    badgeColor: "bg-gold-500",
    image: "/images/products/tirage.png",
    link: "/product/tirage-photo",
  },
  {
    name: "Le YearBook",
    format: "A4",
    pages: "A5",
    features: ["Couverture rigide", "De 24 à 160 pages"],
    price: "22,79€",
    originalPrice: "28,49€",
    discount: "-20%",
    badge: "",
    badgeColor: "bg-rose-400",
    image: "/images/products/yearbook.png",
    link: "/create_book?open=9",
  },
];

export default function ProductsSection() {
  const [showTirageDialog, setShowTirageDialog] = useState(false);

  const handleProductClick = (product: (typeof products)[0]) => {
    if (product.name === "Les Tirages Photos") {
      setShowTirageDialog(true);
    } else {
      window.location.href = product.link;
    }
  };

  return (
    <section id="albums" className="py-12 lg:py-16 bg-background scroll-mt-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-8 lg:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground max-w-4xl mx-auto leading-tight">
            Nos <span className="text-gradient">Albums Photo</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Découvrez notre gamme complète d'albums photo, conçus pour immortaliser vos plus beaux souvenirs.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 pt-8">
          {products.map((product, index) => (
            <div
              key={index}
              onClick={() => handleProductClick(product)}
              className={`product-hover-effect group relative bg-card rounded-2xl shadow-card hover:shadow-elegant transition-all duration-500 border border-border/50 cursor-pointer ${
                product.name === "Le Pix"
                  ? "hover:bg-[#FDF1E2]"
                  : product.name === "L'Album Paysage"
                    ? "hover:bg-[#FCEAE6]"
                    : product.name === "L'Album Carré"
                      ? "hover:bg-[#FDF1E2]"
                      : product.name === "L'Album PREMIUM"
                        ? "hover:bg-[#FCEAE6]"
                        : product.name === "Les Tirages Photos"
                          ? "hover:bg-[#FDF1E2]"
                          : "hover:bg-[#FCEAE6]"
              }`}
            >
              {/* Badge - Ruban en haut à gauche */}
              {product.badge && (
                <div className="absolute -top-2 -left-2 bg-[#f9bb72] text-white px-4 py-2 rounded-2xl text-xs font-semibold z-10 shadow-xl transform -rotate-12 origin-top-left">
                  {product.badge}
                </div>
              )}

              {/* Discount Badge */}
              <div
                className="absolute -top-6 right-4 px-3 py-2 rounded-full text-sm font-bold z-10 shadow-xl border-2 border-white text-white"
                style={{ backgroundColor: "#f7bbac" }}
              >
                {product.discount}
              </div>

              {/* Product Album Image */}
              <div className="w-full h-64 relative overflow-hidden rounded-t-2xl">
                <img
                  src={product.image}
                  alt={`${product.name} - Album photo ${product.format}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              {/* Product Info */}
              <div className="p-6 space-y-4 flex flex-col text-center">
                <h3 className="text-xl font-display font-semibold text-foreground">{product.name}</h3>

                <div className="text-muted-foreground">
                  <p>
                    {product.format} • {product.pages}
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-2 min-h-[50px] flex flex-col items-center">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-muted-foreground">
                      <div className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: "#f9bb72" }}></div>
                      {feature}
                    </div>
                  ))}
                </div>

                {/* Price */}
                <div className="flex flex-col items-center justify-center pt-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-muted-foreground">À partir de</span>
                    <span className="text-2xl font-bold text-foreground">{product.price}</span>
                  </div>
                  {product.originalPrice && (
                    <span className="text-lg text-muted-foreground line-through">{product.originalPrice}</span>
                  )}
                </div>

                {/* CTA Button */}
                <div className="pt-4">
                  <Button 
                    variant="rainbow" 
                    className="w-full" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductClick(product);
                    }}
                  >
                    Choisir
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={showTirageDialog} onOpenChange={setShowTirageDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Produit bientôt disponible</AlertDialogTitle>
            <AlertDialogDescription>
              Le Tirage Photo sera bientôt disponible. Nous travaillons activement sur ce nouveau produit pour vous
              offrir la meilleure qualité possible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Compris</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
